import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { ShieldCheck, ExternalLink } from "lucide-react";

/**
 * SEBI mandates 2-Factor Authentication for mutual fund Subscription
 * (since Apr 2023) and Redemption (since Jul 2022) transactions, and BSE's
 * Nomination Registration API similarly requires client 2FA confirmation.
 * BSE sends the investor to its hosted authentication page. No OTP is ever
 * generated, displayed, or verified by this application.
 */
export function TwoFactorDialog({ open, onClose, title = "BSE Authentication", verificationLink, onVerify }) {
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await onVerify();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="two-factor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-[var(--brand)]">
            <ShieldCheck className="h-5 w-5 text-[var(--cta)]" /> {title}
          </DialogTitle>
          <DialogDescription>
            Complete authentication on BSE&apos;s secure page, then return here so we can verify the exchange status.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {verificationLink ? (
            <Button asChild className="w-full rounded-full bg-[var(--cta)]">
              <a href={verificationLink} target="_blank" rel="noreferrer">Open BSE authentication <ExternalLink className="ml-2 h-4 w-4" /></a>
            </Button>
          ) : (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              BSE has not returned an authentication link yet. Check the transaction status before retrying.
            </div>
          )}
            <Button variant="outline" disabled={loading} onClick={handleVerify} className="w-full rounded-full">
              {loading ? "Checking BSE status..." : "I completed authentication — check status"}
            </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
